import { TxSearchResult, type TxSummary } from '@apm/contracts';
import { useCallback, useEffect, useState } from 'react';
import { getJson, toQuery } from '../api';
import { PAST_LIMIT, PAST_SLICES, sliceLimit, sliceRange } from './past';

/**
 * 과거 보기가 그릴 행을 받아 온다 (23-A회차, 리뷰 2).
 *
 * 두 단계다.
 *   1. `limit=1`로 그 기간에 몇 건이 있는지만 먼저 묻는다. 응답 몸통이 거의 없다
 *   2. 상한 안이면 한 번에 받고, 넘으면 기간을 조각으로 나눠 조각마다 몫만큼 받는다
 *
 * `useQuery`를 쓰지 않는 이유는 조회가 여럿이기 때문이다. 취소·계약 검증은 같은 방식으로 한다 —
 * 화면이 기간을 바꾸면 앞의 조회는 버리고, 늦게 온 답이 새 답을 덮지 않는다.
 */

export interface PastRows {
  loading: boolean;
  error?: string;
  /** 그릴 행. 시각 오름차순 */
  rows: TxSummary[];
  /** 그 기간에 있는 전체 건수(서버가 센 값) */
  total: number;
  /** 실제로 받아 그린 수. 서버 상한이 더 낮으면 몫보다 적게 온다 */
  received: number;
  /** 조각으로 나눠 받았는지. 안내 문구가 이 값으로 갈린다 */
  sampled: boolean;
  days?: number;
  truncated: boolean;
  reload: () => void;
}

const EMPTY: PastRows = {
  loading: false,
  rows: [],
  total: 0,
  received: 0,
  sampled: false,
  truncated: false,
  reload: () => undefined,
};

function searchUrl(
  from: number,
  to: number,
  limit: number,
  service?: string,
  sort?: string,
): string {
  return `/api/tx/search${toQuery({ from, to, limit, service, sort })}`;
}

export function usePastRows(
  range: { from?: number; to?: number } | undefined,
  service?: string,
): PastRows {
  const from = range?.from;
  const to = range?.to;
  const [state, setState] = useState<Omit<PastRows, 'reload'>>(EMPTY);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (from === undefined || to === undefined) {
      setState(EMPTY);
      return;
    }
    const controller = new AbortController();
    const signal = controller.signal;
    setState((prev) => ({ ...prev, loading: true, error: undefined }));

    void (async () => {
      try {
        // 1) 몇 건인지만 먼저. 조각으로 나눌지 여기서 갈린다
        const probe = await getJson(searchUrl(from, to, 1, service), TxSearchResult, signal);
        if (signal.aborted) return;

        const slices =
          probe.total > PAST_LIMIT ? sliceRange(from, to, PAST_SLICES) : [{ from, to }];
        const limit = slices.length > 1 ? sliceLimit(PAST_LIMIT, slices.length) : PAST_LIMIT;
        const pages = await Promise.all(
          slices.map((slice) =>
            getJson(searchUrl(slice.from, slice.to, limit, service), TxSearchResult, signal),
          ),
        );
        if (signal.aborted) return;

        const rows = pages
          .flatMap((page) => page.rows)
          .sort((a, b) => a.startedAt - b.startedAt || a.txId.localeCompare(b.txId));
        setState({
          loading: false,
          rows,
          total: probe.total,
          received: rows.length,
          sampled: slices.length > 1,
          days: probe.days,
          truncated: probe.truncated === true || pages.some((page) => page.truncated === true),
        });
      } catch (err) {
        if (signal.aborted) return;
        setState({
          ...EMPTY,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => controller.abort();
  }, [from, to, service, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);
  return { ...state, reload };
}
